import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete
} from '@mui/material';
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  FileText,
  DollarSign,
  Download,
  Eye,
  CheckCircle,
  Mail,
  MessageCircle
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const QUOTE_STATUS_LABELS = {
  draft: 'Bozza',
  sent: 'Inviato',
  accepted: 'Accettato',
  rejected: 'Rifiutato'
};

const CONTRACT_STATUS_LABELS = {
  draft: 'Bozza',
  active: 'Attivo',
  closed: 'Chiuso',
  terminated: 'Terminato'
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function ContactDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  // Debt Positions State
  const [positions, setPositions] = useState([]);
  const [positionsLoading, setPositionsLoading] = useState(false);
  const [openPositionDialog, setOpenPositionDialog] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null); // for edit
  const [positionForm, setPositionForm] = useState({
    controparte_id: '',
    tipo_rapporto_id: '',
    contract_number: '',
    original_debt_amount: '',
    notes: ''
  });

  // Select Options
  const [controparti, setControparti] = useState([]);
  const [tipiRapporto, setTipiRapporto] = useState([]);

  // Quotes State
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [openQuoteDialog, setOpenQuoteDialog] = useState(false);
  const [quotePreview, setQuotePreview] = useState(null);
  const [quotePreviewLoading, setQuotePreviewLoading] = useState(false);
  const [quoteDialogTab, setQuoteDialogTab] = useState(0);

  // Contracts State
  const [contracts, setContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [openContractDialog, setOpenContractDialog] = useState(false);
  const [contractDetails, setContractDetails] = useState(null);
  const [contractDetailsLoading, setContractDetailsLoading] = useState(false);

  useEffect(() => {
    fetchContact();
    fetchOptions();
  }, [id]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchPositions();
    } else if (tabValue === 2) {
      fetchQuotes();
    } else if (tabValue === 3) {
      fetchContracts();
    }
  }, [tabValue, id]);

  const fetchContact = async () => {
    try {
      const response = await api.get(`/contacts/${id}`);
      setContact(response.data);
    } catch (error) {
      console.error('Error fetching contact:', error);
      toast.error('Errore nel caricamento del contatto');
    } finally {
      setLoading(false);
    }
  };

  const fetchPositions = async () => {
    setPositionsLoading(true);
    try {
      // Use query param or nested route depending on backend implementation
      // We implemented nested route /contacts/:id/debt-positions in backend?
      // Wait, backend implementation:
      // router.use('/:contactId/debt-positions', debtPositionRoutes);
      // debtPositionRoutes uses mergeParams.
      // So GET /api/contacts/:id/debt-positions works.
      const response = await api.get(`/contacts/${id}/debt-positions`);
      setPositions(response.data);
    } catch (error) {
      console.error('Error fetching positions:', error);
      toast.error('Errore nel caricamento delle posizioni');
    } finally {
      setPositionsLoading(false);
    }
  };

  const fetchOptions = async () => {
    try {
      const [contropartiRes, tipiRes] = await Promise.all([
        api.get('/controparti?limit=1000'),
        api.get('/tipi-rapporto')
      ]);
      setControparti(contropartiRes.data.controparti || contropartiRes.data);
      setTipiRapporto(tipiRes.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchQuotes = async () => {
    setQuotesLoading(true);
    try {
      const response = await api.get(`/contacts/${id}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Errore nel caricamento dei preventivi');
    } finally {
      setQuotesLoading(false);
    }
  };

  const handleOpenQuoteDialog = async () => {
    setQuoteDialogTab(0);
    setOpenQuoteDialog(true);
    setQuotePreviewLoading(true);
    setQuotePreview(null);
    try {
      const response = await api.get(`/contacts/${id}/quotes/preview`);
      setQuotePreview(response.data);
    } catch (error) {
      console.error('Error fetching quote preview:', error);
      toast.error('Errore nel calcolo del preventivo. Assicurati che ci siano posizioni e un tabellario attivo.');
      setOpenQuoteDialog(false);
    } finally {
      setQuotePreviewLoading(false);
    }
  };

  const updateQuoteStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}/status`, { status });
      toast.success(`Stato aggiornato a ${QUOTE_STATUS_LABELS[status]}`);
      fetchQuotes();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Errore nell\'aggiornamento dello stato');
    }
  };

  const handleSendEmail = async (quote) => {
    const email = quote.email || (quote.contact_id === contact.id ? contact.email : null);
    
    if (!email) {
      toast.warning('Nessuna email disponibile per questo contatto');
      return;
    }
    const subject = `Preventivo #${quote.id}`;
    const displayName = quote.display_name || contact.display_name || contact.first_name || contact.company_name;
    const body = `Gentile ${displayName},\n\nin allegato il preventivo richiesto.`;
    window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
    
    if (window.confirm('Hai inviato la email? Vuoi aggiornare lo stato del preventivo a "Inviato"?')) {
      await updateQuoteStatus(quote.id, 'sent');
    }
  };

  const handleSendWhatsapp = async (quote) => {
    const phone = quote.phone || (quote.contact_id === contact.id ? contact.phone : null);

    if (!phone) {
      toast.warning('Nessun numero di telefono disponibile per questo contatto');
      return;
    }
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    const displayName = quote.display_name || contact.display_name || contact.first_name || contact.company_name;
    const text = `Gentile ${displayName}, ecco il preventivo richiesto: ...`; 
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`, '_blank');

    if (window.confirm('Hai inviato il messaggio WhatsApp? Vuoi aggiornare lo stato del preventivo a "Inviato"?')) {
      await updateQuoteStatus(quote.id, 'sent');
    }
  };

  const handleViewQuoteDetails = async (quote) => {
    setQuoteDialogTab(0);
    setOpenQuoteDialog(true);
    setQuotePreviewLoading(true);
    setQuotePreview(null);
    try {
      const response = await api.get(`/quotes/${quote.id}`);
      // Normalize items for display compatibility
      const quoteDetails = response.data;
      if (quoteDetails.items) {
        quoteDetails.items = quoteDetails.items.map(item => ({
          ...item,
          debt_position: {
            controparte_name: item.controparte_name,
            tipo_rapporto_name: item.tipo_rapporto_label,
          },
          // fee_rule_used is not stored, so it will be undefined
        }));
      }
      setQuotePreview(quoteDetails);
    } catch (error) {
      console.error('Error fetching quote details:', error);
      toast.error('Errore nel caricamento dei dettagli del preventivo');
      setOpenQuoteDialog(false);
    } finally {
      setQuotePreviewLoading(false);
    }
  };

  const handleGenerateQuote = async () => {
    try {
      const payload = {
        items: quotePreview.items.map(item => ({
          debt_position_id: item.debt_position_id,
          fee_amount: item.fee_amount
        })),
        parties: [
          {
            contact_id: id,
            role: 'INTESTATARIO',
            is_primary: true
          }
        ]
      };
      
      await api.post(`/contacts/${id}/quotes`, payload);
      toast.success('Preventivo generato con successo');
      setOpenQuoteDialog(false);
      fetchQuotes();
    } catch (error) {
      console.error('Error generating quote:', error);
      toast.error('Errore nella generazione del preventivo');
    }
  };

  const handleFeeChange = (index, newValue) => {
    if (!quotePreview || !quotePreview.items) return;
    
    const newItems = [...quotePreview.items];
    const newFee = parseFloat(newValue) || 0;
    
    newItems[index] = {
      ...newItems[index],
      fee_amount: newFee
    };
    
    // Recalculate total fee
    const newTotalFee = newItems.reduce((sum, item) => sum + (parseFloat(item.fee_amount) || 0), 0);
    
    setQuotePreview({
      ...quotePreview,
      items: newItems,
      total_fee: newTotalFee
    });
  };

  const fetchContracts = async () => {
    setContractsLoading(true);
    try {
      const response = await api.get(`/contacts/${id}/contracts`);
      setContracts(response.data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error('Errore nel caricamento dei contratti');
    } finally {
      setContractsLoading(false);
    }
  };

  const handleViewContractDetails = async (contract) => {
    setOpenContractDialog(true);
    setContractDetailsLoading(true);
    setContractDetails(null);
    try {
      const response = await api.get(`/contacts/${id}/contracts/${contract.id}`);
      setContractDetails(response.data);
    } catch (error) {
      console.error('Error fetching contract details:', error);
      toast.error('Errore nel caricamento dei dettagli del contratto');
      setOpenContractDialog(false);
    } finally {
      setContractDetailsLoading(false);
    }
  };

  const handleDownloadContractPdf = async (contractId) => {
    try {
      const response = await api.get(`/contacts/${id}/contracts/${contractId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contratto_${contractId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading contract PDF:', error);
      toast.error('Errore nel download del contratto');
    }
  };

  const handleDownloadQuotePdf = async (quoteId) => {
    try {
      const response = await api.get(`/quotes/${quoteId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `preventivo_${quoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading quote PDF:', error);
      toast.error('Errore nel download del PDF');
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo preventivo?')) return;
    try {
      await api.delete(`/contacts/${id}/quotes/${quoteId}`);
      toast.success('Preventivo eliminato correttamente');
      fetchQuotes();
    } catch (error) {
      console.error('Error deleting quote:', error);
      toast.error('Errore nell\'eliminazione del preventivo');
    }
  };

  const handleCreateContract = async (quoteId) => {
    if (!window.confirm('Vuoi convertire questo preventivo in contratto?')) return;
    try {
      await api.post(`/contacts/${id}/contracts`, { quote_id: quoteId });
      toast.success('Contratto creato con successo');
      fetchQuotes(); 
      setTabValue(3); // Switch to Contracts tab
    } catch (error) {
      console.error('Error creating contract:', error);
      toast.error(error.response?.data?.message || 'Errore nella creazione del contratto');
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleQuoteDialogTabChange = (event, newValue) => {
    setQuoteDialogTab(newValue);
  };

  const handleOpenPositionDialog = (position = null) => {
    if (position) {
      setCurrentPosition(position);
      setPositionForm({
        controparte_id: position.controparte_id,
        tipo_rapporto_id: position.tipo_rapporto_id,
        contract_number: position.contract_number || '',
        original_debt_amount: position.original_debt_amount,
        notes: position.notes || ''
      });
    } else {
      setCurrentPosition(null);
      setPositionForm({
        controparte_id: '',
        tipo_rapporto_id: '',
        contract_number: '',
        original_debt_amount: '',
        notes: ''
      });
    }
    setOpenPositionDialog(true);
  };

  const handleClosePositionDialog = () => {
    setOpenPositionDialog(false);
    setCurrentPosition(null);
  };

  const handleSavePosition = async () => {
    try {
      if (!positionForm.controparte_id || !positionForm.tipo_rapporto_id || !positionForm.original_debt_amount) {
        toast.error('Compila i campi obbligatori');
        return;
      }

      const payload = { ...positionForm, contact_id: id };

      if (currentPosition) {
        await api.put(`/debt-positions/${currentPosition.id}`, payload);
        toast.success('Posizione aggiornata');
      } else {
        await api.post(`/contacts/${id}/debt-positions`, payload);
        toast.success('Posizione aggiunta');
      }
      
      handleClosePositionDialog();
      fetchPositions();
    } catch (error) {
      console.error('Error saving position:', error);
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDeletePosition = async (positionId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa posizione?')) return;
    try {
      await api.delete(`/debt-positions/${positionId}`);
      toast.success('Posizione eliminata');
      fetchPositions();
    } catch (error) {
      console.error('Error deleting position:', error);
      toast.error('Errore nell\'eliminazione');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!contact) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Contatto non trovato</Typography>
        <Button startIcon={<ArrowLeft />} onClick={() => navigate('/contacts')} sx={{ mt: 2 }}>
          Torna alla lista
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowLeft />} onClick={() => navigate('/contacts')} sx={{ mr: 2 }}>
          Indietro
        </Button>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>
          {contact.display_name || `${contact.first_name} ${contact.last_name}` || contact.company_name}
        </Typography>
        <Chip 
          label={contact.status === 'lead' ? 'Lead' : contact.status} 
          color={contact.status === 'lead' ? 'warning' : 'default'} 
        />
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="contact tabs">
          <Tab label="Anagrafica" />
          <Tab label="Posizioni Debitorie" />
          <Tab label="Preventivi" />
          <Tab label="Contratti" />
        </Tabs>
      </Card>

      {/* Anagrafica Panel */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Dati Principali</Typography>
                <Typography><strong>Tipo:</strong> {contact.type === 'individual' ? 'Persona Fisica' : 'Persona Giuridica'}</Typography>
                <Typography><strong>Email:</strong> {contact.email}</Typography>
                <Typography><strong>Telefono:</strong> {contact.phone}</Typography>
                <Typography><strong>Città:</strong> {contact.city_name} ({contact.province})</Typography>
                {contact.notes && (
                   <Box sx={{ mt: 2 }}>
                     <Typography variant="subtitle2">Note Colloquio:</Typography>
                     <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                       {contact.notes}
                     </Typography>
                   </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
          {/* More details... */}
        </Grid>
      </TabPanel>

      {/* Posizioni Debitorie Panel */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<Plus />} 
            onClick={() => handleOpenPositionDialog()}
          >
            Aggiungi Posizione
          </Button>
        </Box>
        
        {positionsLoading ? (
          <CircularProgress />
        ) : positions.length === 0 ? (
          <Typography color="textSecondary">Nessuna posizione debitoria registrata.</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Controparte</TableCell>
                  <TableCell>Tipo Rapporto</TableCell>
                  <TableCell>N. Contratto</TableCell>
                  <TableCell align="right">Debito Residuo</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {positions.map((pos) => (
                  <TableRow key={pos.id}>
                    <TableCell>{pos.controparte_name}</TableCell>
                    <TableCell>{pos.tipo_rapporto_name}</TableCell>
                    <TableCell>{pos.contract_number || '-'}</TableCell>
                    <TableCell align="right">
                      {new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(pos.original_debt_amount)}
                    </TableCell>
                    <TableCell>{pos.notes}</TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleOpenPositionDialog(pos)} color="primary">
                        <Edit size={18} />
                      </IconButton>
                      <IconButton onClick={() => handleDeletePosition(pos.id)} color="error">
                        <Trash2 size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Preventivi Panel */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<Plus />} 
            onClick={handleOpenQuoteDialog}
          >
            Nuovo Preventivo
          </Button>
        </Box>

        {quotesLoading ? (
          <CircularProgress />
        ) : quotes.length === 0 ? (
          <Typography color="textSecondary">Nessun preventivo generato</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Ruolo</TableCell>
                  <TableCell>Data Creazione</TableCell>
                  <TableCell>Stato</TableCell>
                  <TableCell>Totale Debito</TableCell>
                  <TableCell>Totale Compenso</TableCell>
                  <TableCell>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quotes.map((quote) => (
                  <TableRow key={quote.id}>
                    <TableCell>#{quote.id}</TableCell>
                    <TableCell>
                      <Chip 
                        label={quote.contact_role || 'N/A'} 
                        size="small"
                        color={quote.contact_role === 'INTESTATARIO' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{new Date(quote.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip 
                        label={QUOTE_STATUS_LABELS[quote.status] || quote.status} 
                        color={quote.status === 'accepted' ? 'success' : quote.status === 'sent' ? 'info' : 'default'} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>€ {parseFloat(quote.total_debt_amount).toFixed(2)}</TableCell>
                    <TableCell>€ {parseFloat(quote.total_fee_amount).toFixed(2)}</TableCell>
                    <TableCell>
                      {quote.status === 'draft' && (
                        <>
                          <IconButton 
                            title="Invia via Email" 
                            size="small"
                            color="primary"
                            onClick={() => handleSendEmail(quote)}
                          >
                            <Mail size={18} />
                          </IconButton>
                          <IconButton 
                            title="Invia via WhatsApp" 
                            size="small"
                            color="success"
                            onClick={() => handleSendWhatsapp(quote)}
                          >
                            <MessageCircle size={18} />
                          </IconButton>
                        </>
                      )}
                      <IconButton 
                        title="Dettagli" 
                        size="small"
                        onClick={() => handleViewQuoteDetails(quote)}
                      >
                        <Eye size={18} />
                      </IconButton>
                      <IconButton 
                        title="Scarica PDF" 
                        size="small"
                        color="primary"
                        onClick={() => handleDownloadQuotePdf(quote.id)}
                      >
                        <Download size={18} />
                      </IconButton>
                      {quote.status === 'draft' && (
                        <>
                          <IconButton
                            title="Invia via Email"
                            size="small"
                            color="primary"
                            onClick={() => handleSendEmail(quote)}
                          >
                            <Mail size={18} />
                          </IconButton>
                          <IconButton
                            title="Invia via WhatsApp"
                            size="small"
                            color="success"
                            onClick={() => handleSendWhatsapp(quote)}
                          >
                            <MessageCircle size={18} />
                          </IconButton>
                          <IconButton
                            title="Elimina Preventivo"
                            size="small"
                            color="error"
                            onClick={() => handleDeleteQuote(quote.id)}
                          >
                            <Trash2 size={18} />
                          </IconButton>
                        </>
                      )}
                      {quote.status === 'accepted' && (
                         <IconButton 
                           title="Già convertito in contratto" 
                           size="small" 
                           disabled
                           color="success"
                         >
                           <CheckCircle size={18} />
                         </IconButton>
                      )}
                      {quote.status !== 'accepted' && quote.status !== 'rejected' && (
                        <IconButton 
                          title="Converti in Contratto" 
                          size="small"
                          color="primary"
                          onClick={() => handleCreateContract(quote.id)}
                        >
                          <CheckCircle size={18} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Contratti Panel */}
      <TabPanel value={tabValue} index={3}>
        {contractsLoading ? (
          <CircularProgress />
        ) : contracts.length === 0 ? (
          <Typography color="textSecondary">Nessun contratto attivo</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Numero Contratto</TableCell>
                  <TableCell>Data Creazione</TableCell>
                  <TableCell>Stato</TableCell>
                  <TableCell>Importo</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contracts.map((contract) => (
                  <TableRow key={contract.id}>
                    <TableCell>{contract.contract_number}</TableCell>
                    <TableCell>{new Date(contract.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip 
                        label={CONTRACT_STATUS_LABELS[contract.status] || contract.status} 
                        color={contract.status === 'active' ? 'success' : 'default'} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>€ {parseFloat(contract.total_amount).toFixed(2)}</TableCell>
                    <TableCell>{contract.notes || '-'}</TableCell>
                    <TableCell>
                      <IconButton
                        title="Dettagli Contratto"
                        size="small"
                        onClick={() => handleViewContractDetails(contract)}
                      >
                        <Eye size={18} />
                      </IconButton>
                      <IconButton
                        title="Scarica Contratto"
                        size="small"
                        onClick={() => handleDownloadContractPdf(contract.id)}
                      >
                        <Download size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Position Dialog */}
      <Dialog open={openPositionDialog} onClose={handleClosePositionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{currentPosition ? 'Modifica Posizione' : 'Nuova Posizione'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Controparte</InputLabel>
              <Select
                value={positionForm.controparte_id}
                label="Controparte"
                onChange={(e) => setPositionForm({ ...positionForm, controparte_id: e.target.value })}
              >
                {controparti.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.denominazione}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Tipo Rapporto</InputLabel>
              <Select
                value={positionForm.tipo_rapporto_id}
                label="Tipo Rapporto"
                onChange={(e) => setPositionForm({ ...positionForm, tipo_rapporto_id: e.target.value })}
              >
                {tipiRapporto.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Numero Contratto (Opzionale)"
              value={positionForm.contract_number}
              onChange={(e) => setPositionForm({ ...positionForm, contract_number: e.target.value })}
              fullWidth
            />

            <TextField
              label="Importo Debito Residuo"
              type="number"
              value={positionForm.original_debt_amount}
              onChange={(e) => setPositionForm({ ...positionForm, original_debt_amount: e.target.value })}
              fullWidth
              InputProps={{
                startAdornment: <Box component="span" sx={{ mr: 1 }}>€</Box>,
              }}
            />

            <TextField
              label="Note Operative"
              multiline
              rows={3}
              value={positionForm.notes}
              onChange={(e) => setPositionForm({ ...positionForm, notes: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePositionDialog}>Annulla</Button>
          <Button onClick={handleSavePosition} variant="contained" color="primary">
            Salva
          </Button>
        </DialogActions>
      </Dialog>


      {/* Quote Dialog */}
      <Dialog open={openQuoteDialog} onClose={() => setOpenQuoteDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{quotePreview && quotePreview.id ? `Dettagli Preventivo #${quotePreview.id}` : 'Anteprima Preventivo'}</DialogTitle>
        <DialogContent>
          {quotePreviewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : quotePreview ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Tabellario applicato: <strong>{quotePreview.fee_config?.name || 'N/A'}</strong>
              </Typography>

              <Tabs
                value={quoteDialogTab}
                onChange={handleQuoteDialogTabChange}
                sx={{ mt: 2, mb: 2 }}
              >
                <Tab label="Posizioni" />
                <Tab label="Coobbligati / Garanti" />
              </Tabs>

              {quoteDialogTab === 1 && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  {quotePreview.parties && quotePreview.parties.length > 0 ? (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Soggetti del Preventivo
                      </Typography>
                      {quotePreview.parties.map((party) => (
                        <Box
                          key={party.id || `${party.contact_id}-${party.role}`}
                          sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ mr: 1, fontWeight: party.is_primary ? 600 : 400 }}
                          >
                            {party.is_primary ? 'Intestatario' : 'Co-intestatario'}:
                          </Typography>
                          <Typography variant="body2" sx={{ mr: 1 }}>
                            {party.display_name || party.contact_name || `Contatto #${party.contact_id}`}
                          </Typography>
                          <Chip
                            label={party.role || 'ALTRO'}
                            size="small"
                            color={party.is_primary ? 'primary' : 'default'}
                          />
                        </Box>
                      ))}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Nessun coobbligato o garante associato a questo preventivo.
                    </Typography>
                  )}
                </Box>
              )}

              {quoteDialogTab === 0 && (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Posizione</TableCell>
                          <TableCell align="right">Debito</TableCell>
                          <TableCell align="right">Compenso Calcolato</TableCell>
                          <TableCell>Regola Applicata</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {quotePreview.items && quotePreview.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              {item.debt_position?.controparte_name || 'N/A'} - {item.debt_position?.tipo_rapporto_name || 'N/A'}
                            </TableCell>
                            <TableCell align="right">€ {parseFloat(item.debt_amount).toFixed(2)}</TableCell>
                            <TableCell align="right">
                              {!quotePreview.id ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%' }}>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={item.fee_amount}
                                    onChange={(e) => handleFeeChange(idx, e.target.value)}
                                    InputProps={{
                                      startAdornment: <Box component="span" sx={{ mr: 0.5, color: 'text.secondary' }}>€</Box>,
                                    }}
                                    sx={{ width: 120 }}
                                  />
                                  {(item.original_fee_amount !== undefined && item.original_fee_amount !== null) ? (
                                    <Typography variant="body2" sx={{ mt: 0.5, display: 'block', width: '100%', textAlign: 'right', fontSize: '0.75rem' }}>
                                      {Math.abs(parseFloat(item.fee_amount) - parseFloat(item.original_fee_amount)) > 0.01 ? (
                                        <span style={{ color: '#ed6c02', fontWeight: 'bold' }}>
                                          Calc: € {parseFloat(item.original_fee_amount).toFixed(2)}
                                        </span>
                                      ) : (
                                        <span style={{ color: '#666' }}>
                                          Calc: € {parseFloat(item.original_fee_amount).toFixed(2)}
                                        </span>
                                      )}
                                    </Typography>
                                  ) : (
                                    <Typography variant="caption" sx={{ color: 'red' }}>Err: No Orig Fee</Typography>
                                  )}
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                  <Typography>€ {parseFloat(item.fee_amount).toFixed(2)}</Typography>
                                  {item.original_fee_amount !== undefined && item.original_fee_amount !== null && (
                                    <Typography variant="caption" sx={{ 
                                      mt: 0.5, 
                                      display: 'block', 
                                      color: Math.abs(parseFloat(item.fee_amount) - parseFloat(item.original_fee_amount)) > 0.01 ? '#ed6c02' : '#666',
                                      fontWeight: Math.abs(parseFloat(item.fee_amount) - parseFloat(item.original_fee_amount)) > 0.01 ? 'bold' : 'normal',
                                      textDecoration: Math.abs(parseFloat(item.fee_amount) - parseFloat(item.original_fee_amount)) > 0.01 ? 'line-through' : 'none'
                                    }}>
                                      Calc: € {parseFloat(item.original_fee_amount).toFixed(2)}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                              {item.fee_rule_used ? (
                                <>
                                  {item.fee_rule_used.tipo_rapporto_label ? item.fee_rule_used.tipo_rapporto_label : 'Generica'}
                                  <br/>
                                  (Fisso: €{item.fee_rule_used.fixed_fee} + {item.fee_rule_used.variable_fee_percent}%)
                                </>
                              ) : (quotePreview.id ? '-' : 'Nessuna regola trovata')}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell><strong>TOTALE</strong></TableCell>
                          <TableCell align="right"><strong>€ {quotePreview.total_debt_amount ? parseFloat(quotePreview.total_debt_amount).toFixed(2) : parseFloat(quotePreview.total_debt).toFixed(2)}</strong></TableCell>
                          <TableCell align="right"><strong>€ {quotePreview.total_fee_amount ? parseFloat(quotePreview.total_fee_amount).toFixed(2) : parseFloat(quotePreview.total_fee).toFixed(2)}</strong></TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Typography variant="body2" color="text.secondary">
                    * Il compenso è calcolato in base alle regole del tabellario attivo.
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            <Typography>Impossibile caricare l'anteprima.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenQuoteDialog(false)}>Chiudi</Button>
          {(!quotePreview || !quotePreview.id) && (
            <Button 
              onClick={handleGenerateQuote} 
              variant="contained" 
              color="primary"
              disabled={!quotePreview || !quotePreview.items || quotePreview.items.length === 0}
            >
              Conferma e Genera
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Contract Dialog */}
      <Dialog open={openContractDialog} onClose={() => setOpenContractDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {contractDetails ? `Dettagli Contratto #${contractDetails.contract_number}` : 'Dettagli Contratto'}
        </DialogTitle>
        <DialogContent>
          {contractDetailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : contractDetails ? (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Numero contratto: <strong>{contractDetails.contract_number}</strong>
                </Typography>
                <Typography variant="body2">
                  Stato:{' '}
                  <Chip
                    label={CONTRACT_STATUS_LABELS[contractDetails.status] || contractDetails.status}
                    color={contractDetails.status === 'active' ? 'success' : 'default'}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                {contractDetails.created_at && (
                  <Typography variant="body2">
                    Creato il: {new Date(contractDetails.created_at).toLocaleDateString()}
                  </Typography>
                )}
                {contractDetails.start_date && (
                  <Typography variant="body2">
                    Data inizio: {new Date(contractDetails.start_date).toLocaleDateString()}
                  </Typography>
                )}
                {contractDetails.end_date && (
                  <Typography variant="body2">
                    Data fine: {new Date(contractDetails.end_date).toLocaleDateString()}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Importo totale: <strong>€ {parseFloat(contractDetails.total_amount || 0).toFixed(2)}</strong>
                </Typography>
              </Box>

              {contractDetails.parties && contractDetails.parties.length > 0 && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Soggetti del Contratto
                  </Typography>
                  {contractDetails.parties.map((party) => (
                    <Box
                      key={party.id || `${party.contact_id}-${party.role}`}
                      sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ mr: 1, fontWeight: party.is_primary ? 600 : 400 }}
                      >
                        {party.is_primary ? 'Intestatario' : 'Co-intestatario'}:
                      </Typography>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        {party.display_name || party.contact_name || `Contatto #${party.contact_id}`}
                      </Typography>
                      <Chip
                        label={party.role || 'ALTRO'}
                        size="small"
                        color={party.is_primary ? 'primary' : 'default'}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              {contractDetails.notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Note
                  </Typography>
                  <Typography variant="body2">{contractDetails.notes}</Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography>Impossibile caricare i dettagli del contratto.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenContractDialog(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
